import React, { useState, useEffect, useRef } from 'react';
import { Trophy, Lock } from 'lucide-react';

export const XP_PER_VOCAB   = 10;
export const XP_PER_KANJI   = 10;
export const XP_PER_GRAMMAR = 10;
export const XP_PER_REVIEW_PASS = 5;
export const XP_PER_REVIEW_FAIL = 1;

export function calcXPFromProgress(masteredVocab, masteredKanji, masteredGrammar) {
  return (masteredVocab.length + masteredKanji.length + masteredGrammar.length) * XP_PER_VOCAB;
}

export function xpToLevel(xp) {
  return Math.min(100, Math.floor(Math.sqrt(xp / 50)) + 1);
}

export function xpForLevel(level) {
  return Math.pow(level - 1, 2) * 50;
}

export function xpProgress(xp) {
  const level   = xpToLevel(xp);
  const current = xpForLevel(level);
  const next    = xpForLevel(level + 1);
  const pct     = next > current ? Math.round(((xp - current) / (next - current)) * 100) : 100;
  return { level, current: xp - current, needed: next - current, pct };
}

export const ACHIEVEMENTS = [
  { id: 'vocab_10',   icon: '📖', title: 'First Words',     desc: 'Master 10 vocabulary words',      xp: 50,  check: (s) => s.vocab >= 10   },
  { id: 'vocab_50',   icon: '📚', title: 'Word Hoarder',    desc: 'Master 50 vocabulary words',      xp: 100, check: (s) => s.vocab >= 50   },
  { id: 'vocab_100',  icon: '🗂️', title: 'Vocabulary Pro',  desc: 'Master 100 vocabulary words',     xp: 200, check: (s) => s.vocab >= 100  },
  { id: 'vocab_500',  icon: '🏛️', title: 'Lexicon Master',  desc: 'Master 500 vocabulary words',     xp: 500, check: (s) => s.vocab >= 500  },
  { id: 'kanji_10',   icon: '漢', title: 'Kanji Starter',   desc: 'Master 10 kanji characters',      xp: 50,  check: (s) => s.kanji >= 10   },
  { id: 'kanji_50',   icon: '字', title: 'Kanji Seeker',    desc: 'Master 50 kanji characters',      xp: 100, check: (s) => s.kanji >= 50   },
  { id: 'kanji_100',  icon: '🔤', title: 'Kanji Scholar',   desc: 'Master 100 kanji characters',     xp: 200, check: (s) => s.kanji >= 100  },
  { id: 'grammar_10', icon: '✍️', title: 'Grammar Novice',  desc: 'Master 10 grammar points',        xp: 50,  check: (s) => s.grammar >= 10 },
  { id: 'grammar_50', icon: '📝', title: 'Grammar Guru',    desc: 'Master 50 grammar points',        xp: 150, check: (s) => s.grammar >= 50 },
  { id: 'streak_3',   icon: '🔥', title: 'On Fire',         desc: 'Maintain a 3-day streak',          xp: 30,  check: (s) => s.streak >= 3   },
  { id: 'streak_7',   icon: '⚡', title: 'Weekly Warrior',  desc: 'Maintain a 7-day streak',          xp: 75,  check: (s) => s.streak >= 7   },
  { id: 'streak_30',  icon: '💎', title: 'Diamond Learner', desc: 'Maintain a 30-day streak',         xp: 300, check: (s) => s.streak >= 30  },
  { id: 'review_10',  icon: '🔁', title: 'Reviewer',        desc: 'Complete 10 SRS review sessions', xp: 50,  check: (s) => s.reviews >= 10  },
  { id: 'review_50',  icon: '🧠', title: 'Memory Palace',   desc: 'Complete 50 SRS review sessions', xp: 150, check: (s) => s.reviews >= 50  },
  { id: 'total_50',   icon: '⭐', title: 'Rising Star',     desc: 'Master 50 items total',            xp: 100, check: (s) => s.total >= 50   },
  { id: 'total_200',  icon: '🌟', title: 'All-Rounder',     desc: 'Master 200 items total',           xp: 300, check: (s) => s.total >= 200  },
  { id: 'total_500',  icon: '🏆', title: 'JLPT Champion',   desc: 'Master 500 items total',           xp: 750, check: (s) => s.total >= 500  },
];

function AchievementToast({ achievement, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 4000); return () => clearTimeout(t); }, [onDone]);
  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-500 pointer-events-none">
      <div className="flex items-center gap-3 px-5 py-3.5 rounded-2xl"
        style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out)', color: 'var(--text-primary)' }}>
        <span className="text-2xl">{achievement.icon}</span>
        <div>
          <p className="text-[9px] font-black uppercase tracking-widest mb-0.5" style={{ color: 'var(--accent)' }}>Achievement Unlocked!</p>
          <p className="text-sm font-black leading-none" style={{ color: 'var(--text-primary)' }}>{achievement.title}</p>
          <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-muted)' }}>+{achievement.xp} XP</p>
        </div>
      </div>
    </div>
  );
}

export function XPBar({ xp, className = '' }) {
  const { level, pct, current, needed } = xpProgress(xp);
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <div className="flex items-center justify-center w-7 h-7 rounded-full bg-violet-600 text-white text-[10px] font-black shrink-0">{level}</div>
      <div className="flex-1 min-w-[60px]">
        <div className="h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
        </div>
        <p className="text-[8px] font-black text-slate-400 mt-0.5 leading-none">{current}/{needed} XP</p>
      </div>
    </div>
  );
}

export default function AchievementSystem({
  isOpen, onClose,
  masteredVocab = [], masteredKanji = [], masteredGrammar = [],
  streak = 0, reviewCount = 0,
}) {
  const stats = {
    vocab:   masteredVocab.length,
    kanji:   masteredKanji.length,
    grammar: masteredGrammar.length,
    streak,
    reviews: reviewCount,
    total:   masteredVocab.length + masteredKanji.length + masteredGrammar.length,
  };

  const xp = calcXPFromProgress(masteredVocab, masteredKanji, masteredGrammar);
  const { level, pct, current, needed } = xpProgress(xp);

  const unlocked = ACHIEVEMENTS.filter(a => a.check(stats));
  const locked   = ACHIEVEMENTS.filter(a => !a.check(stats));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center animate-in fade-in duration-200"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(6px)' }}>
      <div className="absolute inset-0" onClick={onClose} />
      <div className="relative m-auto w-full max-w-lg flex flex-col animate-in zoom-in-95 duration-200"
        style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out)', borderRadius: 28, maxHeight: 'min(680px, calc(100dvh - 1rem))' }}>

        {/* Accent stripe */}
        <div className="h-1 w-full shrink-0" style={{ background: 'var(--accent)', borderRadius: '28px 28px 0 0' }} />

        {/* Header */}
        <div className="px-5 pt-4 pb-3 shrink-0">
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="text-[8px] font-black uppercase tracking-widest mb-0.5" style={{ color: 'var(--accent)' }}>Your Progress</p>
              <h2 className="text-lg font-black tracking-tight" style={{ color: 'var(--text-primary)' }}>Achievements & XP</h2>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center transition-all hover:opacity-70 shrink-0"
              style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out-sm)', color: 'var(--text-muted)' }}>✕</button>
          </div>

          {/* Level + XP bar */}
          <div className="rounded-2xl px-4 py-3 mb-3" style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-in-sm)' }}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg font-black shrink-0"
                style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out-sm)', color: 'var(--accent)' }}>{level}</div>
              <div>
                <p className="font-black text-sm leading-none" style={{ color: 'var(--text-primary)' }}>Level {level}</p>
                <p className="text-[9px] font-bold" style={{ color: 'var(--text-muted)' }}>{xp.toLocaleString()} XP</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[8px] font-bold" style={{ color: 'var(--text-muted)' }}>Next level</p>
                <p className="font-black text-xs" style={{ color: 'var(--text-primary)' }}>{current}/{needed} XP</p>
              </div>
            </div>
            <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--track-bg)' }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-4 gap-2">
            {[
              { label: 'Vocab',   value: stats.vocab },
              { label: 'Kanji',   value: stats.kanji },
              { label: 'Grammar', value: stats.grammar },
              { label: 'Streak',  value: `${streak}d` },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl py-2 text-center" style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-in-sm)' }}>
                <p className="text-sm font-black leading-none" style={{ color: 'var(--text-primary)' }}>{value}</p>
                <p className="text-[7px] font-black uppercase tracking-widest mt-0.5" style={{ color: 'var(--text-muted)' }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Achievements list */}
        <div className="overflow-y-auto flex-1 px-5 pb-5" style={{ WebkitOverflowScrolling: 'touch' }}>
          {unlocked.length > 0 && (
            <>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Unlocked · {unlocked.length}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                {unlocked.map(a => (
                  <div key={a.id} className="flex items-center gap-2.5 p-3 rounded-xl"
                    style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-out-sm)', borderLeft: '3px solid var(--accent)' }}>
                    <span className="text-lg shrink-0">{a.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-xs truncate" style={{ color: 'var(--text-primary)' }}>{a.title}</p>
                      <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>{a.desc}</p>
                    </div>
                    <span className="shrink-0 text-[9px] font-black px-1.5 py-0.5 rounded-full"
                      style={{ background: 'var(--accent)', color: '#000' }}>+{a.xp}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {locked.length > 0 && (
            <>
              <p className="text-[8px] font-black uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>Locked · {locked.length}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {locked.map(a => (
                  <div key={a.id} className="flex items-center gap-2.5 p-3 rounded-xl opacity-50"
                    style={{ background: 'var(--bg-primary)', boxShadow: 'var(--neu-in-sm)' }}>
                    <span className="text-lg shrink-0 grayscale">{a.icon}</span>
                    <div className="min-w-0 flex-1">
                      <p className="font-black text-xs truncate" style={{ color: 'var(--text-primary)' }}>{a.title}</p>
                      <p className="text-[9px] truncate" style={{ color: 'var(--text-muted)' }}>{a.desc}</p>
                    </div>
                    <Lock size={11} className="shrink-0" style={{ color: 'var(--text-muted)' }} />
                  </div>
                ))}
              </div>
            </>
          )}

          {unlocked.length === 0 && locked.length === 0 && (
            <div className="text-center py-8" style={{ color: 'var(--text-muted)' }}>
              <Trophy size={32} className="mx-auto mb-2 opacity-30" />
              <p className="font-bold text-sm">Start learning to earn achievements!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function useAchievementToasts(stats) {
  const [toast, setToast] = useState(null);
  const prevUnlocked = useRef(new Set());

  useEffect(() => {
    const nowUnlocked = ACHIEVEMENTS.filter(a => a.check(stats)).map(a => a.id);
    const newOnes = nowUnlocked.filter(id => !prevUnlocked.current.has(id));
    if (newOnes.length > 0) {
      const achievement = ACHIEVEMENTS.find(a => a.id === newOnes[0]);
      if (achievement) setToast(achievement);
      newOnes.forEach(id => prevUnlocked.current.add(id));
    } else {
      nowUnlocked.forEach(id => prevUnlocked.current.add(id));
    }
  }, [stats]);

  return toast ? <AchievementToast achievement={toast} onDone={() => setToast(null)} /> : null;
}
